import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";

interface SeasonalOfferBuilderProps {
  companyId?: string;
  services: { id: string; name: string }[];
  onCreate: (payload: any) => void;
}

export default function SeasonalOfferBuilder({ companyId: companyIdProp, services, onCreate }: SeasonalOfferBuilderProps) {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [discount, setDiscount] = useState(10);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [isActive, setIsActive] = useState<boolean>(true);
  const { toast } = useToast();

  const publish = async () => {
    // Prefer the explicit companyId passed from the dashboard
    let companyId = companyIdProp;
    if (!companyId) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: t('offers.not_signed_in'), description: t('offers.sign_in_to_publish'), variant: 'destructive' });
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('companies(id)')
        .eq('user_id', session.user.id)
        .single();
      companyId = (profile as any)?.companies?.[0]?.id;
    }
    if (!companyId) {
      toast({ title: t('offers.no_company'), description: t('offers.complete_onboarding'), variant: 'destructive' });
      return;
    }
    const payload = {
      company_id: companyId,
      service_id: serviceId,
      name,
      discount_percent: discount,
      start_date: format(startDate!, 'yyyy-MM-dd'),
      end_date: format(endDate!, 'yyyy-MM-dd'),
      is_active: isActive,
    };
    const { error } = await supabase.from('seasonal_offers').insert(payload);
    if (error) {
      toast({ title: t('offers.publish_failed'), description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: t('offers.published'), description: t('offers.live') });
    onCreate(payload);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>{t('offers.title')}</CardTitle>
        <CardDescription>{t('offers.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 max-w-full overflow-x-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
          <Input placeholder={t('offers.placeholder.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger className="min-w-0">
              <SelectValue placeholder={t('offers.placeholder.service')} />
            </SelectTrigger>
            <SelectContent>
              {services.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground mb-1">{t('offers.fields.start_date')}</div>
            <div className="overflow-x-hidden">
              <Calendar mode="single" selected={startDate} onSelect={setStartDate} className="rounded-md border w-full max-w-full" />
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground mb-1">{t('offers.fields.end_date')}</div>
            <div className="overflow-x-hidden">
              <Calendar mode="single" selected={endDate} onSelect={setEndDate} className="rounded-md border w-full max-w-full" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-sm text-muted-foreground mb-1">{t('offers.fields.discount')}</div>
            <Input type="number" value={discount} onChange={(e) => setDiscount(parseInt(e.target.value) || 0)} />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> {t('offers.fields.active')}
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={publish} disabled={!name || !serviceId || !startDate || !endDate}>{t('offers.publish')}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

